import { ErrorBoundary, type ErrorBoundaryProps } from '@sentry/react';
import { Menu } from 'lucide-react';
import { useState, type ReactNode, Suspense, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Outlet } from 'react-router';
import Footer from '../../blocks/Footer';
import MobileSidebar from './MobileSidebar';
import { handleError } from '@/components/blocks/Error';
import Loader from '@/components/common/Loader';
import { NavbarItem } from '@/components/common/NavBar';
import BreadcrumbRenderer from '@/contexts/BreadcrumbContext';

const beforeCapture: ErrorBoundaryProps['beforeCapture'] = scope => {
    scope.setTag('boundary', 'StackedLayout');
};

// Fullscreen Context for main content area only
interface FullscreenContextType {
    isFullscreen: boolean;
    toggleFullscreen: () => void;
    enterFullscreen: () => void;
    exitFullscreen: () => void;
    isMobile: boolean; // Add mobile detection to context
}

const FullscreenContext = createContext<FullscreenContextType | null>(null);

export const useFullscreen = () => {
    const context = useContext(FullscreenContext);
    if (!context) {
        throw new Error('useFullscreen must be used within a FullscreenProvider');
    }
    return context;
};

export interface StackedLayoutProps {
    navbar: ReactNode;
    sidebar: ReactNode;
    isMultiCard?: boolean;
}

const StackedLayout = ({ navbar, sidebar, isMultiCard = false }: StackedLayoutProps) => {
    const [showSidebar, setShowSidebar] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(64);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMobile, setIsMobile] = useState(() => {
        // Initialize based on window width to avoid hydration mismatch
        if (typeof window !== 'undefined') {
            return window.innerWidth < 1024;
        }
        return false;
    });
    const mainContentRef = useRef<HTMLDivElement>(null);
    const contentAreaRef = useRef<HTMLDivElement>(null);

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 1024; // lg breakpoint
            setIsMobile(mobile);
        };

        // Check on mount
        checkMobile();

        // Check on resize
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fullscreen management for main content area only
    const enterFullscreen = useCallback(async () => {
        // Disable fullscreen on mobile
        if (isMobile) {
            console.log('Fullscreen not supported on mobile devices');
            return;
        }

        if (contentAreaRef.current && contentAreaRef.current.requestFullscreen) {
            try {
                await contentAreaRef.current.requestFullscreen();
                setIsFullscreen(true);
            } catch (error) {
                console.error('Failed to enter fullscreen:', error);
            }
        }
    }, [isMobile]);

    const exitFullscreen = useCallback(async () => {
        // Disable fullscreen on mobile
        if (isMobile) {
            return;
        }

        if (document.exitFullscreen) {
            try {
                await document.exitFullscreen();
                setIsFullscreen(false);
            } catch (error) {
                console.error('Failed to exit fullscreen:', error);
            }
        }
    }, [isMobile]);

    const toggleFullscreen = useCallback(() => {
        // Disable fullscreen on mobile
        if (isMobile) {
            return;
        }

        if (isFullscreen) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    }, [isFullscreen, exitFullscreen, enterFullscreen, isMobile]);

    // Listen for fullscreen changes (only on desktop)
    useEffect(() => {
        if (isMobile) {
            return; // Don't listen for fullscreen changes on mobile
        }

        const handleFullscreenChange = () => {
            const newFullscreenState = !!document.fullscreenElement;
            setIsFullscreen(newFullscreenState);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [isMobile]);

    // Create a ResizeObserver to watch the sidebar container's width
    useEffect(() => {
        const sidebarContainer = document.querySelector('.sidebar-container');
        if (!sidebarContainer) {
            console.warn('Sidebar container not found. ResizeObserver will not be initialized.');
            return;
        }

        // Create a ResizeObserver to track sidebar width changes
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                // Get the new width and update state
                const newWidth = entry.contentRect.width;
                setSidebarWidth(newWidth);
            }
        });

        // Start observing
        resizeObserver.observe(sidebarContainer);

        // Clean up
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Also listen for localStorage changes for permanent sidebar state (desktop only)
    useEffect(() => {
        // Only handle sidebar width on desktop
        if (isMobile) {
            return;
        }

        const handleStorageChange = () => {
            const expanded = localStorage.getItem('sidebarExpanded') === 'true';
            setSidebarWidth(expanded ? 256 : 64);
        };

        // Check initial state on mount
        handleStorageChange();

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [isMobile]);

    const fullscreenContextValue: FullscreenContextType = {
        isFullscreen: isMobile ? false : isFullscreen, // Always false on mobile
        toggleFullscreen,
        enterFullscreen,
        exitFullscreen,
        isMobile,
    };

    return (
        <div className="relative flex min-h-svh w-full bg-neutral-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
            {/* Sidebar on mobile */}
            <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
                {sidebar}
            </MobileSidebar>

            {/* Desktop Sidebar - Only visible on larger screens */}
            <div className="z-10 hidden lg:block">{sidebar}</div>

            {/* Main content container - Dynamic margin based on sidebar width */}
            <div
                ref={mainContentRef}
                className="flex w-full flex-1 flex-col transition-all duration-300"
                style={
                    !isMobile ? { marginLeft: `${sidebarWidth}px`, width: `calc(100vw - ${sidebarWidth + 16}px)` } : {}
                }
            >
                {/* Navbar - Only visible on smaller screens */}
                <header className="fixed z-10 flex w-full items-center bg-white px-4 shadow-xs lg:hidden">
                    <div className="py-2.5">
                        <NavbarItem onClick={() => setShowSidebar(true)} aria-label="Open navigation">
                            <Menu className="size-6 text-neutral-500" />
                        </NavbarItem>
                    </div>
                    <div className="min-w-0 flex-1">{navbar}</div>
                </header>

                {/* Content */}
                <main className="flex flex-1 flex-col gap-6 pt-[68px] lg:px-4 lg:pt-4">
                    <BreadcrumbRenderer />

                    {/* 
                        Content alignment is controlled by the individual components.
                        Components can use Tailwind classes like:
                        - Default: no special classes (content starts from top)
                        - For center alignment: use "flex items-center justify-center min-h-full"
                        - For bottom alignment: use "flex items-end justify-center min-h-full"
                    */}
                    <FullscreenContext.Provider value={fullscreenContextValue}>
                        {isMultiCard ? (
                            <ErrorBoundary beforeCapture={beforeCapture} fallback={handleError}>
                                <Suspense fallback={<Loader />}>
                                    <Outlet />
                                </Suspense>
                            </ErrorBoundary>
                        ) : (
                            <div
                                ref={contentAreaRef}
                                className="mx-4 mt-4 flex grow overflow-auto rounded-lg bg-white p-5 lg:m-0 dark:lg:bg-zinc-900"
                            >
                                <div className="w-full">
                                    <ErrorBoundary beforeCapture={beforeCapture} fallback={handleError}>
                                        <Suspense fallback={<Loader />}>
                                            <Outlet />
                                        </Suspense>
                                    </ErrorBoundary>
                                </div>
                            </div>
                        )}
                    </FullscreenContext.Provider>

                    <Footer layoutStyle="StackedLayout" />
                </main>
            </div>
        </div>
    );
};

export default StackedLayout;
